import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Table, Alert, Modal, Form, Card, Nav, Tab } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useNotification } from "../context/NotificationContext";
import ConfirmModal from "../components/ConfirmModal";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: "",
    date: "",
    location: "",
    description: "",
    image: null,
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberFormData, setMemberFormData] = useState({
    name: "",
    email: "",
    role: "member"
  });
  const [confirmState, setConfirmState] = useState({ show: false, action: null, data: null, title: "", message: "" });

  useEffect(() => {
    fetchEvents();
    fetchUsers();
  }, []);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/activities?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data.activities || []);
    } catch (err) {
      setError("Failed to fetch activity logs.");
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/events`);
      setEvents(response.data);
    } catch (err) {
      setError("Failed to fetch events.");
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data);
    } catch (err) {
      setError("Failed to fetch members. Check your login status.");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_API_URL}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setEvents(events.filter((event) => event._id !== eventId));
      notify("Event deleted successfully!", "success");
    } catch (error) {
      setError("Failed to delete event.");
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_API_URL}/auth/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(users.filter((user) => user._id !== userId));
      notify("Member removed successfully!", "success");
    } catch (error) {
      setError("Failed to remove member.");
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setMemberFormData({
      name: member.name,
      email: member.email,
      role: member.role
    });
    setShowMemberModal(true);
  };

  const handleSaveMember = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.put(`${process.env.REACT_APP_API_URL}/auth/members/${selectedMember._id}`,
        memberFormData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        fetchUsers();
        setShowMemberModal(false);
        setSelectedMember(null);
        setMemberFormData({ name: "", email: "", role: "member" });
        notify("Member updated successfully!", "success");
      }
    } catch (error) {
      setError("Failed to update member.");
    }
  };

  const handleCancelMemberEdit = () => {
    setSelectedMember(null);
    setMemberFormData({ name: "", email: "", role: "member" });
    setShowMemberModal(false);
  };

  const handleResetPassword = async (email) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/reset-password`, { email });
      notify("Reset password link sent successfully.", "success");
    } catch (error) {
      setError("Failed to send reset password link.");
    }
  };

  const handleSaveEvent = async () => {
    try {
      const token = localStorage.getItem("token");

      let requestData;
      let headers = { Authorization: `Bearer ${token}` };

      if (newEvent.image) {
        requestData = new FormData();
        requestData.append("title", newEvent.title);
        requestData.append("date", new Date(newEvent.date + 'T12:00:00').toISOString());
        requestData.append("location", newEvent.location);
        requestData.append("description", newEvent.description);
        requestData.append("image", newEvent.image);
      } else {
        requestData = {
          title: newEvent.title,
          date: new Date(newEvent.date + 'T12:00:00').toISOString(),
          location: newEvent.location,
          description: newEvent.description
        };
        headers["Content-Type"] = "application/json";
      }

      let response;

      if (editingEvent) {
        if (requestData instanceof FormData) {
          response = await fetch(`${process.env.REACT_APP_API_URL}/events/${editingEvent._id}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
            body: requestData,
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Update failed');
          }

          response = await response.json();
        } else {
          response = await axios.put(`${process.env.REACT_APP_API_URL}/events/${editingEvent._id}`, requestData, {
            headers,
          });
          response = response.data;
        }

        notify("Event updated successfully!", "success");
      } else {
        if (requestData instanceof FormData) {
          response = await fetch(`${process.env.REACT_APP_API_URL}/events`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: requestData,
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Creation failed');
          }

          response = await response.json();
        } else {
          response = await axios.post(`${process.env.REACT_APP_API_URL}/events`, requestData, {
            headers,
          });
          response = response.data;
        }

        notify("Event created successfully!", "success");
      }

      setNewEvent({ title: "", date: "", location: "", description: "", image: null });
      setEditingEvent(null);
      setShowEventModal(false);
      fetchEvents();
    } catch (error) {
      setError(`Failed to ${editingEvent ? 'update' : 'create'} event: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    const localDate = new Date(event.date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    setNewEvent({
      title: event.title,
      date: formattedDate,
      location: event.location,
      description: event.description || "",
      image: null
    });
    setShowEventModal(true);
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setNewEvent({ title: "", date: "", location: "", description: "", image: null });
    setShowEventModal(false);
  };

  const requestConfirm = (action, data, title, message) => {
    setConfirmState({ show: true, action, data, title, message });
  };

  const handleConfirm = () => {
    const { action, data } = confirmState;
    if (action === "deleteEvent") handleDeleteEvent(data);
    else if (action === "removeMember") handleRemoveMember(data);
    setConfirmState({ show: false, action: null, data: null, title: "", message: "" });
  };

  return (
    <Container className="py-5">
      <h2 className="mb-4 text-center fw-bold section-heading">Admin Dashboard</h2>

      {error && <Alert variant="danger" className="text-center" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Tab.Container defaultActiveKey="events">
        <Row>
          {/* Sidebar Navigation */}
          <Col md={3}>
            <Card className="p-3 shadow border-0 admin-sidebar">
              <h5 className="fw-bold text-center section-heading">Admin Panel</h5>
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link eventKey="events" className="my-1">
                    Manage Events
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="members" className="my-1">
                    Manage Members
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="activity" className="my-1" onClick={fetchActivities}>
                    Activity Logs
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <hr />
              <Button
                variant="outline-success"
                className="w-100"
                onClick={() => navigate("/financials")}
              >
                Go to Financials
              </Button>
            </Card>
          </Col>

          {/* Tab Content */}
          <Col md={9}>
            <Tab.Content>
              {/* Events Management */}
              <Tab.Pane eventKey="events">
                <Card className="p-4 shadow border-0">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="fw-bold section-heading">Upcoming Events</h4>
                    <Button
                      className="btn-brand-navy"
                      onClick={() => setShowEventModal(true)}
                    >
                      Add Event
                    </Button>
                  </div>
                  {events.length === 0 ? (
                    <Alert variant="info" className="text-center">No events available. Create your first event!</Alert>
                  ) : (
                    <Table striped bordered hover responsive className="text-center align-middle">
                      <thead>
                        <tr>
                          <th>Event Name</th>
                          <th>Date</th>
                          <th>Location</th>
                          <th>Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event) => (
                          <tr key={event._id}>
                            <td className="fw-bold">{event.title}</td>
                            <td>{new Date(event.date).toLocaleDateString()}</td>
                            <td>{event.location}</td>
                            <td>{event.description.length > 50 ? `${event.description.substring(0, 50)}...` : event.description}</td>
                            <td>
                              <Button
                                variant="warning"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditEvent(event)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => requestConfirm("deleteEvent", event._id, "Delete Event", `Are you sure you want to delete "${event.title}"?`)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Tab.Pane>

              {/* Members Management */}
              <Tab.Pane eventKey="members">
                <Card className="p-4 shadow-lg border-0">
                  <h4 className="mb-3 fw-bold text-center">Manage Members</h4>
                  {users.length === 0 ? (
                    <Alert variant="info" className="text-center">No members available.</Alert>
                  ) : (
                    <Table striped bordered hover responsive className="text-center align-middle">
                      <thead className="bg-dark text-white">
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Date Added</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user._id}>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.role}</td>
                            <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "No date available"}</td>
                            <td>
                              <Button variant="warning" size="sm" onClick={() => handleEditMember(user)}>
                                Edit
                              </Button>{" "}
                              <Button variant="danger" size="sm" onClick={() => requestConfirm("removeMember", user._id, "Remove Member", `Are you sure you want to remove ${user.name}?`)}>
                                Remove
                              </Button>{" "}
                              <Button variant="info" size="sm" onClick={() => handleResetPassword(user.email)}>
                                Reset Password
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Tab.Pane>

              {/* Activity Logs */}
              <Tab.Pane eventKey="activity">
                <Card className="p-4 shadow-lg border-0">
                  <h4 className="mb-3 fw-bold text-center">Activity Logs</h4>
                  {activities.length === 0 ? (
                    <Alert variant="info" className="text-center">No activity logs available.</Alert>
                  ) : (
                    <Table striped bordered hover responsive className="text-center align-middle">
                      <thead className="bg-dark text-white">
                        <tr>
                          <th>Date</th>
                          <th>Action</th>
                          <th>Description</th>
                          <th>Performed By</th>
                          <th>Target User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.map((activity) => (
                          <tr key={activity._id}>
                            <td>{new Date(activity.createdAt).toLocaleDateString()}</td>
                            <td>
                              <span className="badge bg-primary">{activity.action}</span>
                            </td>
                            <td className="text-start">{activity.description}</td>
                            <td>{activity.performedBy?.name || 'System'}</td>
                            <td>{activity.targetUser?.name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>

      {/* Event Creation/Edit Modal */}
      <Modal show={showEventModal} onHide={handleCancelEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingEvent ? 'Edit Event' : 'Create New Event'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Event Title</Form.Label>
              <Form.Control
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                placeholder="Enter event title"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                value={newEvent.location}
                onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                placeholder="Enter event location"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                placeholder="Enter event description"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Event Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setNewEvent({...newEvent, image: e.target.files[0]})}
              />
              <Form.Text className="text-muted">
                Upload an image for the event (optional)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button
            className="btn-brand-navy"
            onClick={handleSaveEvent}
            disabled={!newEvent.title || !newEvent.date || !newEvent.location}
          >
            {editingEvent ? 'Update Event' : 'Create Event'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Member Edit Modal */}
      <Modal show={showMemberModal} onHide={handleCancelMemberEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                value={memberFormData.name}
                onChange={(e) => setMemberFormData({...memberFormData, name: e.target.value})}
                placeholder="Enter full name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                value={memberFormData.email}
                onChange={(e) => setMemberFormData({...memberFormData, email: e.target.value})}
                placeholder="Enter email address"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={memberFormData.role}
                onChange={(e) => setMemberFormData({...memberFormData, role: e.target.value})}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelMemberEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveMember}
            disabled={!memberFormData.name || !memberFormData.email}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        show={confirmState.show}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel="Confirm"
        variant="danger"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmState({ show: false, action: null, data: null, title: "", message: "" })}
      />
    </Container>
  );
};

export default AdminDashboard;
